import type { AutopayDetailsResponse } from "@credit-acceptance/step6-bff-client";
import { Fragment, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import type en from "../../../../../languages/en.json";
import { formatIsoDate } from "../../../../../lib/format";
import { formatCurrency } from "../../../../../lib/money";
import { SubsectionHeader } from "./SubsectionHeader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

type AutoPayFieldKey = keyof typeof en.review.autoPay.fields;

interface AutoPayInformationProperties {
  personPath: PersonPath;
  /**
   * Deal-level AutoPay details from the dashboard response (recap-service).
   * Undefined when the read failed — the BFF omits the section and reports a
   * `metadata.warnings[section=autopay]` entry instead.
   */
  autopay?: AutopayDetailsResponse;
}

/**
 * Maps the AutoPay response onto ordered [i18n field key, display value]
 * rows, dropping any row whose value is absent (e.g. second debit fields on a
 * monthly schedule, card type on a bank account). Bank-only rows are also
 * dropped when recap-service says not to show bank info.
 * @param {AutopayDetailsResponse} autopay - The AutoPay details for the deal.
 * @returns {Array<[AutoPayFieldKey, string]>} Rows to render, in display order.
 */
function toAutoPayRows(autopay: AutopayDetailsResponse): Array<[AutoPayFieldKey, string]> {
  const showBankInfo = autopay.showBankInfo !== false;
  const rows: Array<[AutoPayFieldKey, string | undefined]> = [
    ["bankAccountHolder", autopay.payerName],
    ["accountType", autopay.accountType],
    ["accountStatus", autopay.accountStatus],
    ["bankAccountNumber", autopay.lastFourAccountNumber],
    ["bankName", showBankInfo ? autopay.bankName : undefined],
    ["bankType", showBankInfo ? autopay.bankType : undefined],
    ["cardType", autopay.cardType],
    ["periodicPayment", autopay.debitAmount === undefined ? undefined : formatCurrency(autopay.debitAmount)],
    ["debitFrequency", autopay.debitFrequency],
    ["firstPaymentDueDate", formatIsoDate(autopay.firstPaymentDueDate)],
    ["scheduleDay", autopay.firstScheduleDay],
    ["secondScheduleDay", autopay.secondScheduleDay],
    ["firstDebitDate", formatIsoDate(autopay.dateOfFirstDebit)],
    ["secondDebitDate", formatIsoDate(autopay.dateOfSecondDebit)],
  ];
  return rows.filter((row): row is [AutoPayFieldKey, string] => Boolean(row[1]));
}

// ---------------------------------------------------------------------------
// AutoPayInformation
//
// Collapsible per-person subsection rendering the deal's AutoPay details as a
// read-only list. Unlike BankInformation, there is nothing to bind/validate —
// the data comes straight from the dashboard response's `autopay` section.
// ---------------------------------------------------------------------------
export function AutoPayInformation({ personPath, autopay }: AutoPayInformationProperties) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  // Unique id per mount — prevents aria-controls collisions when both driver
  // and secondSigner columns are rendered in the same document.
  const uid = useId();
  const contentId = `${personPath}-autopay-info-${uid}-content`;

  function handleToggle() {
    setIsOpen((previous) => !previous);
  }

  /**
   * @TEMP: Temporary cancel handler — swap for WorkbenchAutoPayApi.cancelAutopay
   * once the cancel flow is wired up.
   */
  function handleCancel() {
    window.alert(t("review.autoPay.cancelAlert"));
  }
  // `cancellable` reflects recap-service eligibility only — it does not
  // account for the user's role or the VLA lock.
  const isDisabled = !autopay?.cancellable;
  const hasAccount = Boolean(autopay?.hasAutopayAccount);
  const rows = autopay && hasAccount ? toAutoPayRows(autopay) : [];

  return (
    <div>
      <SubsectionHeader
        title={t("review.autoPay.heading")}
        isOpen={isOpen}
        onToggle={handleToggle}
        contentId={contentId}
      />

      {isOpen && (
        <div id={contentId} className="p-(--spacing-3) gap-(--spacing-4) flex flex-col">
          {!autopay && <p className="text-muted-foreground text-xs">{t("review.autoPay.notEnrolled")}</p>}

          {hasAccount && (
            <>
              <div className="text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) gap-(--spacing-3) gap-y-(--spacing-2) grid grid-cols-[max-content_1fr]">
                {rows.map(([key, value]) => (
                  <Fragment key={key}>
                    <span className="text-muted-foreground font-(--font-weight-medium)">
                      {t(`review.autoPay.fields.${key}`)}:
                    </span>
                    <span className="text-foreground">{value}</span>
                  </Fragment>
                ))}
              </div>

              <Button type="button" size="sm" onClick={handleCancel} className="w-fit" disabled={isDisabled}>
                {t("review.autoPay.cancelButton")}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
